import { AgGridReact } from "ag-grid-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Accordion from "./common/Accordion";
import Loader from "rsuite/esm/Loader";
import Badge from "rsuite/esm/Badge";
import { getFormattedDate, isEmpty } from "../../../../actions/common";
import NoDataFound from "./common/NoDataFound";
import {
  useParams,
} from "react-router-dom/cjs/react-router-dom";

const CARD_BRAND_STYLES = {
  visa: {
    bg: "#1a237e",
    color: "#fff",
    label: "VISA",
  },
  mastercard: {
    bg: "#ff6f00",
    color: "#fff",
    label: "MasterCard",
  },
  default: {
    bg: "#e0e0e0",
    color: "#333",
    label: "CARD",
  },
};


const normalizeBrand = (brand = "") =>
  brand.toLowerCase().replace(/\s|_/g, "");


const RevenueBillingPanel = ({ userTokens, graphqlRequest, customerDetails, appDetails, ismuvi5 }) => {

  const { customer_identifier } = useParams();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [failedPayments, setFailedPayments] = useState([]);
  const [muvi5Payments, setMuvi5Payments] = useState([]);
  const [loading, setLoading] = useState(false)


  useEffect(() => {
    if (ismuvi5) {
      const rows = buildMuvi5PaymentRows();
      setMuvi5Payments(rows);
      setPaymentHistory([])
    } else {
      setMuvi5Payments([])
    }
  }, [customerDetails, ismuvi5]);


  // const hasFetchedRef = useRef(false);

  const graphqlurl = `${customerDetails.enterprise_api_gateway_url ? customerDetails.enterprise_api_gateway_url :process.env.REACT_APP_APIGETWAY}/customer/billing`;

  const fetchPaymentHistory = useCallback(async () => {
    const query = `
      {
        paymentHistory(
          app_token: ":app_token",
          user_uuid: ":me",
          account_type: :account_type,
          per_page: 20,
          page: 1
        ) {
          payment_history_list {
            order_uuid
            item_description
            card_last_four_digits
            transaction_date
            billing_amount
            currency_code
            transaction_status
          }
        }
      }
    `;
    // const graphqlurl = `${process.env.REACT_APP_APIGETWAY}/customer/billing`;
    const data = await graphqlRequest(graphqlurl, { query });

    setPaymentHistory(
      data && data.paymentHistory && data.paymentHistory.payment_history_list ? data.paymentHistory.payment_history_list : []
    );
  }, [graphqlRequest]);

  const fetchFailedPayments = useCallback(async () => {
    const query = "\n  {\n    userPaymentFailureDetails(\n      app_token:\":app_token\",\n      user_uuid:\":me\",\n      account_type::account_type\n    ){\n      plan_name\n      plan_price\n      currency_symbol\n      plan_duration\n      billing_period\n      billing_order_uuid\n      plan_uuid\n      pricing_uuid\n      item_name item_description\n      billing_amount\n      transaction_date\n      payment_type billing_order_details {\n        item_name\n        item_description\n        billing_amount\n      }\n      payment_method\n    }\n  }";
    const data = await graphqlRequest(graphqlurl, { query });

    // setFailedPayments(
    //   data && data.userPaymentFailureDetails ? data.userPaymentFailureDetails : {}
    // );
  }, [graphqlRequest]);

  const fetchPaymentMethods = useCallback(async () => {
    const url = `${customerDetails.enterprise_api_gateway_url ? customerDetails.enterprise_api_gateway_url : process.env.REACT_APP_APIGETWAY}/gateway`;
    const query = "\n  {\n    cards (\n      app_token:\":app_token\",\n      product_key:\":product_key\",\n      user_uuid:\":me\"\n    ) {\n      page_info {\n        total_count\n      }\n      cards_list {\n        card_uuid\n        name\n        brand\n        masked_card\n        is_primary\n        last4\n        created_date\n        last_transaction_date\n      }\n    }\n  }";
    const data = await graphqlRequest(url, { query });

    setPaymentMethods(
      data && data.cards && data.cards.cards_list ? data.cards.cards_list : []
    );
  }, [graphqlRequest]);

  useEffect(
    () => {
      if (Object.keys(appDetails).length === 0) {
        return
      }
      setPaymentMethods(appDetails && appDetails.cards_list ? appDetails.cards_list : [])
    }, [appDetails])

  const tokenRef = useRef(null)

  useEffect(() => {
    if (
      !userTokens
    ) {
      setPaymentMethods([])
      setFailedPayments([])
      setPaymentHistory([])
      return
    }
    if (userTokens === tokenRef.current) return

    tokenRef.current = userTokens;

    const fetchAll = async () => {
      try {
        setLoading(true)
        await Promise.all([
          // fetchSubscriptionDetails(),
          fetchPaymentHistory(),
          fetchFailedPayments(),
          fetchPaymentMethods(),
        ]);
        // hasFetchedRef.current = true;
      } catch (err) {
        console.error("Billing fetch error:", err);
      } finally {
        setLoading(false)
      }
    };

    fetchAll();

  }, [userTokens, ismuvi5]);

  const CardNumberRenderer = (params) => {
    const data = params && params.data;

    if (!data || !data.last4) {
      return "N/A";
    }

    const brandKey = normalizeBrand(data.brand);
    const style =
      CARD_BRAND_STYLES[brandKey] || CARD_BRAND_STYLES.default;

    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        {/* Brand badge */}
        <Badge
          content={style.label}
          style={{
            backgroundColor: style.bg,
            color: style.color,
            padding: "4px 8px",
            borderRadius: 4,
            fontWeight: 600,
            fontSize: 12,
          }}
        />

        {/* Card text */}
        <span style={{ fontSize: 14 }}>
          card ending in <strong>{data.last4}</strong>
        </span>

        {/* Primary badge */}
        {data.is_primary === true && (
          <Badge
            content="Primary Card"
            style={{
              backgroundColor: "#e6f0ff",
              color: "#1675e0",
              padding: "4px 10px",
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 500,
            }}
          />
        )}
      </div>
    );
  };

  // const buildSubscriptionRows = useCallback(function (list) {
  //   if (!Array.isArray(list)) return [];

  //   return list.map(function (item) {
  //     return {
  //       product: item.product_name || "N/A",
  //       status:
  //         item.account_status ||
  //         item.plan_status ||
  //         item.subscription_status ||
  //         "N/A",
  //       plan: item.plan_name || "N/A",
  //       term:
  //         item.plan_duration === "Biweekly"
  //           ? "N/A"
  //           : item.plan_duration || "N/A",

  //       billing_period: item.billing_period || "N/A",
  //       next_billing_date:
  //         item.next_billing_date ||
  //         item.next_infra_billing_date ||
  //         "N/A",
  //       payment:
  //         item.plan_price !== null && item.plan_price !== undefined
  //           ? (item.currency_symbol || "") + item.plan_price
  //           : "N/A",
  //       applications: Array.isArray(item.applications)
  //         ? item.applications.join(", ")
  //         : "N/A",
  //     };
  //   });
  // }, []);

  const buildPaymentRows = useCallback(function (list) {
    if (!Array.isArray(list)) return [];

    return list.map(function (item) {
      return {
        order_uuid: item.order_uuid || "N/A",
        item_description: item.item_description || "N/A",
        card_last_four_digits: item.card_last_four_digits
          ? "Card ending in **** " + item.card_last_four_digits
          : "N/A",
        transaction_date: item.transaction_date || "N/A",
        billing_amount:
          item.billing_amount !== null && item.billing_amount !== undefined
            ? (item.currency_code || "") + item.billing_amount
            : "N/A",
        transaction_status: item.transaction_status || "N/A",
      };
    });
  }, []);

  const buildPaymentMethodRows = useCallback((list) => {
    if (!Array.isArray(list)) return [];

    return list.map((item) => ({
      name: item.name || item.card_holder_name || "N/A",
      last4: item.last4 || item.card_last_fourdigit.replace(/\D/g, "") || null,
      brand: item.brand || item.card_type || "N/A",
      is_primary: item.is_primary === 1,
      created_date: getFormattedDate(item.created_date, "MMM, dd yyyy") || "N/A",
    }));
  }, []);

  const buildMuvi5PaymentRows = useCallback(function () {

    return [
      {
        package:
          (customerDetails && customerDetails.package) ||
          (appDetails && appDetails.package_name) ||
          "N/A",

        payment_method:
          (customerDetails && customerDetails.payment_type) ||
          (appDetails && appDetails.payment_method) ||
          "N/A",

        amount: "N/A",
      },
    ];
  }, [ismuvi5, customerDetails, appDetails]);



  // const subscriptionColumnDefs = useMemo(
  //   function () {
  //     return [
  //       { headerName: "Product", field: "product", width: 180 },
  //       { headerName: "Status", field: "status", width: 120 },
  //       { headerName: "Plan", field: "plan", width: 180 },
  //       { headerName: "Term", field: "term", width: 120 },
  //       { headerName: "Billing Period", field: "billing_period", width: 220 },
  //       { headerName: "Next Billing Date", field: "next_billing_date", width: 160 },
  //       { headerName: "Payment", field: "payment", width: 120 },
  //       { headerName: "Applications", field: "applications", width: 260 },
  //     ];
  //   },
  //   []
  // );

  const paymentColumnDefs = useMemo(
    function () {
      return [
        { headerName: "Bill ID", field: "order_uuid", width: 200 },
        { headerName: "Description", field: "item_description", width: 220 },
        {
          headerName: "Payment Method",
          field: "card_last_four_digits",
          width: 220,
        },
        { headerName: "Date", field: "transaction_date", width: 140 },
        { headerName: "Amount", field: "billing_amount", width: 120 },
        {
          headerName: "Status",
          field: "transaction_status",
          width: 120,
          cellRenderer: function (params) {
            return (
              <Badge
                color={params.value === "Success" ? "green" : "red"}
                content={params.value || "N/A"}
                style={{ padding: "2px 8px", marginTop: 8, textAlign: "center" }}
              />
            );
          },
        },
      ];
    },
    []
  );

  const muvi5paymentColumnDefs = useMemo(
    function () {
      return [
        { headerName: "Package", field: "package", width: 200 },
        {
          headerName: "Payment Method",
          field: "payment_method",
          width: 220,
        },
        // { headerName: "Date", field: "transaction_date", width: 140 },
        { headerName: "Amount", field: "amount", width: 120 },
        // {
        //   headerName: "Status",
        //   field: "transaction_status",
        //   width: 120,
        //   cellRenderer: function (params) {
        //     return (
        //       <Badge
        //         color={params.value === "Success" ? "green" : "red"}
        //         content={params.value || "N/A"}
        //         style={{ padding: "2px 8px", marginTop: 8, textAlign: "center" }}
        //       />
        //     );
        //   },
        // },
      ];
    },
    []
  );

  const paymentMethodColumnDefs = useMemo(() => [
    {
      headerName: "Name on Card",
      field: "name",
      width: 300,
    },
    {
      headerName: "Card Number",
      field: "last4",
      width: 420,
      cellRenderer: CardNumberRenderer,
    },
    {
      headerName: "Card Added On",
      field: "created_date",
      width: 160,
    },
  ], []);


  const failedPaymentColumnDefs = useMemo(
    function () {
      return [
        { headerName: "Bill ID", field: "order_uuid", width: 200 },
        { headerName: "Description", field: "item_description", width: 220 },
        {
          headerName: "Payment Method",
          field: "card_last_four_digits",
          width: 220,
        },
        { headerName: "Date", field: "transaction_date", width: 140 },
        { headerName: "Amount", field: "billing_amount", width: 120 },
        {
          headerName: "Status",
          field: "transaction_status",
          width: 120,
          cellRenderer: function (params) {
            return (
              <Badge
                color={params.value === "Success" ? "green" : "red"}
                content={params.value || "N/A"}
                style={{ padding: "2px 12px", marginTop: 8 }}
              />
            );
          },
        },
      ];
    },
    []
  );

  const paymentRowData = buildPaymentRows(
    paymentHistory
      ? paymentHistory
      : []
  );

  const paymentMethodRowData = buildPaymentMethodRows(
    paymentMethods ? paymentMethods : []
  );

  // const infraRowData = Object.values(infraDetails || {}).map((item) => ({
  //   name: item.name || "N/A",
  //   total: item.total ||  "N/A",
  //   consumed: item.consumed ||  "N/A",
  //   available: item.available || "N/A",
  //   percentage: item.percentage || "N/A",
  //   extra: item.extra || "N/A",
  // }));

  return (
    <div role="tabpanel" className="tab-pane" id="revenue-and-billing">
      <div className="flex flex-col gap-12">
        <div style={{ backgroundColor: "white" }}>
          {loading && (
            <div className="accordion-component p-8 flex justify-center">
              <Loader content="Loading Content..." />
            </div>
          )}
          {
            !loading &&
            paymentMethodRowData.length === 0 &&
            paymentRowData.length === 0 &&
            muvi5Payments.length === 0 &&
            failedPayments.length ===0 && (
              <NoDataFound />
            )
          }
        </div>
        {/* {!loading &&
          Object.entries(subscriptionList || {}).map(function ([key, value], index) {
            if (!Array.isArray(value)) return null;

            return (
              <Accordion
                key={key}
                header="Products"
                targetID={"subscription-" + index}
              >
                <div className="ag-theme-alpine" style={{ height: 220 }}>
                  <AgGridReact
                    columnDefs={subscriptionColumnDefs}
                    rowData={buildSubscriptionRows(value)}
                    defaultColDef={{ resizable: true, sortable: true }}
                    rowHeight={40}
                    suppressContextMenu
                  />
                </div>
              </Accordion>
            );
          })} */}

        {!loading && paymentRowData.length > 0 && (
          <Accordion header="Payment History" targetID="payment-history">
            <div className="ag-theme-alpine" style={{ height: 220 }}>
              <AgGridReact
                columnDefs={paymentColumnDefs}
                rowData={paymentRowData}
                defaultColDef={{ resizable: true, sortable: true }}
                rowHeight={40}
                suppressContextMenu
              />
            </div>
          </Accordion>
        )}

        {ismuvi5 && muvi5Payments.length > 0 && (
          <Accordion header="Payment History" targetID="payment-history">
            <div className="ag-theme-alpine" style={{ height: 220 }}>
              <AgGridReact
                columnDefs={muvi5paymentColumnDefs}
                rowData={muvi5Payments}
                defaultColDef={{ resizable: true, sortable: true }}
                rowHeight={40}
                suppressContextMenu
              />
            </div>
          </Accordion>
        )}


        {/* {!loading && failedPayments.length > 0 && (
          <Accordion header="Failed Payment History" targetID="failed-payment-history">
            <div className="ag-theme-alpine" style={{ height: 220 }}>
              <AgGridReact
                columnDefs={failedPaymentColumnDefs}
                rowData={buildPaymentRows(failedPayments)}
                defaultColDef={{ resizable: true, sortable: true }}
                rowHeight={40}
                suppressContextMenu
              />
            </div>
          </Accordion>
        )} */}

        {!loading && paymentMethodRowData.length > 0 && (
          <Accordion header="Payment Method" targetID="payment-method">
            <div className="ag-theme-alpine" style={{ height: 130 }}>
              <AgGridReact
                columnDefs={paymentMethodColumnDefs}
                rowData={paymentMethodRowData}
                defaultColDef={{ resizable: true, sortable: true }}
                rowHeight={40}
                suppressContextMenu
              />
            </div>
          </Accordion>
        )}

        {/* {!loading && infraRowData.length > 0 && (
          <Accordion header="Infra Usage" targetID="infra-usage">
            <div className="ag-theme-alpine" style={{ height: 300 }}>
              <AgGridReact
                columnDefs={infraColumnDefs}
                rowData={infraRowData}
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                }}
                rowHeight={40}
                suppressContextMenu
              />
            </div>
          </Accordion>
        )} */}


      </div>
    </div>
  );
};

export default RevenueBillingPanel;



// WEBPACK FOOTER //
// ./src/components/crm/leads/components/RevenueBillingPanel.js